import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HERO_BG, PRICE_NEW, PRICE_OLD } from "../../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.95, delay: 0.35 + i * 0.14, ease: EASE },
  }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 26 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, delay, ease: EASE },
  }),
};

export const Hero = ({ onBuy }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <header
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[92svh] flex-col overflow-hidden bg-choc-deep lg:min-h-screen"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={HERO_BG}
          alt="Ciepłe wnętrze restauracji w naturalnym świetle"
          data-testid="hero-background-image"
          className="h-[120%] w-full object-cover"
          initial={{ scale: 1.14 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-choc-deep/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-choc-deep/90 via-choc-deep/35 to-choc-deep/55" />

      <motion.nav
        style={{ opacity: contentOpacity }}
        className="relative z-10 flex items-center justify-between px-6 pt-7 sm:px-10"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
      >
        <span data-testid="brand-wordmark" className="font-display text-lg italic text-cream sm:text-xl">
          Bezpieczna Restauracja
        </span>
        <button
          data-testid="nav-cta-button"
          onClick={onBuy}
          className="hidden rounded-full border border-cream/30 px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-cream hover:text-choc sm:inline-flex"
        >
          Zabezpiecz lokal — {PRICE_NEW}
        </button>
      </motion.nav>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 flex flex-1 flex-col justify-center px-6 py-20 sm:px-10 lg:px-16"
      >
        <motion.span
          data-testid="hero-kicker"
          className="mb-7 inline-flex w-fit items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/80 sm:text-xs"
          variants={fadeVariant}
          custom={0.15}
          initial="hidden"
          animate="show"
        >
          <span className="h-2 w-2 rounded-full bg-brick-soft" />
          Pakiet prawny dla gastronomii
        </motion.span>

        <h1
          data-testid="hero-headline"
          className="max-w-4xl font-display text-4xl font-semibold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {["Sanepid i PIP mogą", "przyjść tego samego dnia.", "Czy masz dokumenty na oba?"].map(
            (line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-brick-soft" : ""}`}
                  variants={lineVariant}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ),
          )}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          className="mt-7 max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg"
          variants={fadeVariant}
          custom={0.95}
          initial="hidden"
          animate="show"
        >
          Restauracje i lokale gastronomiczne są kontrolowane podwójnie — pod
          kątem higieny i pod kątem zatrudnienia. Jeden pakiet, dwa
          zabezpieczenia.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col items-start gap-5"
          variants={fadeVariant}
          custom={1.15}
          initial="hidden"
          animate="show"
        >
          <button
            data-testid="hero-cta-button"
            onClick={onBuy}
            className="group inline-flex items-center gap-3 rounded-full bg-choc px-9 py-5 text-base font-semibold text-white shadow-2xl shadow-choc-deep/50 transition-colors duration-300 hover:bg-choc-deep sm:text-lg"
          >
            Zabezpiecz swój lokal teraz
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <p data-testid="hero-price" className="flex items-baseline gap-3">
            <s className="text-lg text-white/45">{PRICE_OLD}</s>
            <span className="font-display text-3xl font-semibold text-white">{PRICE_NEW}</span>
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        className="relative z-10 flex justify-center pb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 0.8 }}
      >
        <span className="flex flex-col items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-cream/60">
          Przewiń
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </span>
      </motion.div>
    </header>
  );
};
